import { Link } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';

function Retail() {
  return (
    <div className="pt-28">
      {/* Hero Section with Background Image */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1441986300917-64674bd600d8?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
        ></div>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ShoppingBag className="w-16 h-16 text-gold mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Retail <span className="text-gold">Construction</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Construction services for retail stores, shopping centers, and branded environments
          </p>
        </div>
      </section>

      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
            <p>
              Retail construction demands speed, precision, and unwavering attention to brand standards. Williams Diversified specializes in delivering turnkey retail stores, shopping center buildouts, and branded environments on aggressive schedules without compromising quality or craftsmanship.
            </p>
            <p>
              We understand that every day a store isn't open is lost revenue. Our retail construction expertise includes nationwide rollouts, prototype construction, tenant improvements, and fast-track remodels designed to get you open for business as quickly as possible.
            </p>
            
            <h3 className="text-3xl font-bold text-white mt-12 mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Our Capabilities</h3>
            <ul className="space-y-4">
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>National and regional store rollout programs</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Big box and anchor store construction</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Specialty retail and boutique buildouts</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Shopping center and mall tenant improvements</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Store remodels and refresh programs</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Fixture installation and merchandising</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Brand standards compliance and prototype execution</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Facility Maintenance Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-8 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Retail <span className="text-gold">Facility Maintenance</span>
          </h3>
          <p className="text-gray-300 text-lg leading-relaxed text-center mb-8">
            Keep your retail locations operating at peak performance with maintenance services designed to protect your brand image and customer experience.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6">
              <h4 className="text-gold font-bold text-xl mb-3">Store Maintenance</h4>
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start"><span className="text-gold mr-2">•</span>HVAC & climate control service</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Lighting & electrical repairs</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Flooring & ceiling maintenance</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Storefront & entrance repairs</li>
              </ul>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6">
              <h4 className="text-gold font-bold text-xl mb-3">Multi-Location Programs</h4>
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Nationwide service coverage</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Brand standards compliance</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Emergency response 24/7</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Refresh & remodel programs</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* National Rollout Programs Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-4 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            National <span className="text-gold">Rollout Programs</span>
          </h3>
          <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">
            Scalable construction solutions for multi-location retail brands requiring consistent quality and brand execution nationwide
          </p>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">50+</div>
              <div className="text-gray-300 font-medium mb-2">States Covered</div>
              <p className="text-gray-500 text-sm">Nationwide service capability with local expertise</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">1</div>
              <div className="text-gray-300 font-medium mb-2">Point of Contact</div>
              <p className="text-gray-500 text-sm">Single account manager for all locations</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">100%</div>
              <div className="text-gray-300 font-medium mb-2">Brand Consistency</div>
              <p className="text-gray-500 text-sm">Prototype execution across every store</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">24/7</div>
              <div className="text-gray-300 font-medium mb-2">Support</div>
              <p className="text-gray-500 text-sm">Round-the-clock emergency response</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-4 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Why Choose Us for <span className="text-gold">Retail Construction</span>
          </h3>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Building retail environments that drive sales and customer loyalty
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">01</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Brand Standards Expertise</h4>
                <p className="text-gray-400 text-sm mb-4">Meticulous attention to prototype specifications and brand guidelines. Consistent execution whether it's one store or one hundred.</p>
              </div>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">02</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Speed to Market</h4>
                <p className="text-gray-400 text-sm mb-4">Fast-track construction to meet grand opening dates. Every day counts in retail—we deliver on time, every time.</p>
              </div>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">03</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Minimal Disruption</h4>
                <p className="text-gray-400 text-sm mb-4">For remodels in operating stores, we work nights and weekends to protect your sales and customer experience.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-12 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Our Retail <span className="text-gold">Construction Process</span>
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">1</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Prototype Review</h4>
              <p className="text-gray-400 text-sm">Brand standards analysis and prototype specifications review for complete compliance.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">2</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Fast-Track Planning</h4>
              <p className="text-gray-400 text-sm">Aggressive schedule development with milestone dates aligned to your opening.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">3</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Rapid Buildout</h4>
              <p className="text-gray-400 text-sm">Efficient construction execution with extended hours to compress schedules.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">4</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Store Opening</h4>
              <p className="text-gray-400 text-sm">Fixture installation, final inspections, and store setup for opening day success.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Build Your Retail <span className="text-gold">Success</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            Partner with a contractor who understands retail construction
          </p>
          <Link to="/contact" className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider">Contact Us</Link>
        </div>
      </section>
    </div>
  );
}

export default Retail;
